import { computed, Directive, effect, inject, input, signal, viewChild } from '@angular/core';
import { rxResource, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { map, switchMap } from 'rxjs/operators';
import { NavigationService } from '@cue/navigation-services';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { TranslocoService } from '@jsverse/transloco';
import { remove } from 'remove-accents';
import { QRCategoryItemModel } from '@cue/api';
import { FormControl } from '@angular/forms';
import { cuePackResource, NavigationBasicInfoWithFirstLetter } from '@cue/data';
import { TargetFilterData } from './start-target-filter/start-target-filter.component';
import { combineLatest, merge } from 'rxjs';

@Directive()
export class BaseSelection {
  navigationService = inject(NavigationService);
  translocoService = inject(TranslocoService);

  search = new FormControl<string>('', { nonNullable: true });
  searchSignal = toSignal(this.search.valueChanges, {
    initialValue: this.search.value,
  });

  selectedLanguage = toSignal(this.translocoService.langChanges$, {
    initialValue: this.translocoService.getActiveLang(),
  });

  kioskId = input<string>();
  appId = input<string>();

  entity = computed(() => ({
    kioskId: this.kioskId(),
    appId: this.appId(),
  }));

  categories = cuePackResource({
    params: () => {
      return this.entity();
    },
    stream: ({ params }) => this.navigationService.getCategoriesList({ kioskId: params.kioskId, appId: params.appId }),
  });

  private readonly categoryIconUrls = computed(
    () => new Map((this.categories.value() ?? []).map((category) => [category.qrCategoryId, category.iconUrl]))
  );

  selectedCategoryIdList = signal<string[]>([]);
  selectedResourceTypeIdList = signal<number[]>([]);
  selectedAreaIdList = signal<number[]>([]);

  scrollViewport = viewChild(CdkVirtualScrollViewport);
  currentVisibleLetter = signal<string>('');

  overlaysIds = signal<number[]>([]);
  failedImageUrls = signal<ReadonlySet<string>>(new Set());

  activeFiltersCount = computed(() => {
    return (
      this.selectedCategoryIdList().length + this.selectedResourceTypeIdList().length + this.selectedAreaIdList().length
    );
  });

  addOverlay(resourceId: number) {
    this.overlaysIds.update((old) => {
      if (!old.includes(resourceId)) {
        return [...old, resourceId];
      }
      return old;
    });
  }

  scrollToLetter(letter: string, firstIndex: number) {
    if (firstIndex > -1) {
      this.currentVisibleLetter.set(letter);
      this.scrollAlphabetToLetter(letter);
      this.scrollViewport().scrollToIndex(firstIndex, 'instant');
    }
  }

  onScrolledIndexChange(
    index: number,
    alphabetInfo: {
      letter: string;
      available: boolean;
      firstIndex: number;
      count: number;
    }[]
  ) {
    // find letter of the current item or the closest previous item with a letter
    const letterEntry = Object.entries(alphabetInfo).find(
      ([_, info]) => index >= info.firstIndex && index < info.firstIndex + info.count
    );

    if (letterEntry[1] && letterEntry[1].letter !== this.currentVisibleLetter()) {
      this.currentVisibleLetter.set(letterEntry[1].letter);
      this.scrollAlphabetToLetter(letterEntry[1].letter);
    }
  }

  removeOverlay(resourceId: number) {
    this.overlaysIds.update((old) => old.filter((x) => x !== resourceId));
  }

  imageLoadFailed(url: string) {
    this.failedImageUrls.update((failedUrls) => {
      if (failedUrls.has(url)) {
        return failedUrls;
      }

      const updatedFailedUrls = new Set(failedUrls);
      updatedFailedUrls.add(url);
      return updatedFailedUrls;
    });
  }

  hasImageLoadFailed(url: string): boolean {
    return this.failedImageUrls().has(url);
  }

  protected getCategoryIconUrl(item: Pick<NavigationBasicInfoWithFirstLetter, 'qrCodes'>): string | null {
    if (item.qrCodes.length !== 1) {
      return null;
    }

    const categoryId = item.qrCodes[0].qrCategoryCodeId;
    const categoryIconUrl = categoryId ? this.categoryIconUrls().get(categoryId) : null;

    return categoryIconUrl && !this.hasImageLoadFailed(categoryIconUrl) ? categoryIconUrl : null;
  }

  scrollAlphabetToLetter(letter: string) {
    const container = document.querySelector('.alphabet-container');
    if (!container) return;

    const targetLink = Array.from(container.querySelectorAll('a')).find((link) => link.textContent?.trim() === letter);

    if (targetLink) {
      const containerRect = container.getBoundingClientRect();
      const linkRect = targetLink.getBoundingClientRect();
      const scrollTop =
        container.scrollTop + (linkRect.top - containerRect.top) - containerRect.height / 2 + linkRect.height / 2;

      container.scrollTo({ top: scrollTop, behavior: 'smooth' });
    }
  }

  clearAreaFilter() {
    this.selectedAreaIdList.set([]);
  }

  clearTypeFilter() {
    this.selectedResourceTypeIdList.set([]);
  }

  clearCategoryFilter() {
    this.selectedCategoryIdList.set([]);
  }

  clearCategoryAndResourceFilterSelection() {
    this.selectedCategoryIdList.set([]);
    this.selectedResourceTypeIdList.set([]);
  }

  getIndexOfSelectedItem(itemId: string | number, selectedList: string[] | number[]): number {
    return selectedList.findIndex((x) => x == itemId);
  }

  trackNavigationBasicInfoWithFirstLetter(index: number, item: NavigationBasicInfoWithFirstLetter) {
    return item.resourceId || item.qrCodeId || item.letter || index;
  }

  normalizeText(text: string): string {
    if (text) {
      return remove(text.toUpperCase());
    }
    return '';
  }

  filterToSpecificResourceType(category: QRCategoryItemModel) {
    if (category != null) {
      const indexOfSelectedItem = this.getIndexOfSelectedItem(
        category.resourceTypeId,
        this.selectedResourceTypeIdList()
      );
      if (indexOfSelectedItem > -1) {
        this.selectedResourceTypeIdList.set(
          this.selectedResourceTypeIdList().filter((x) => x != category.resourceTypeId)
        );
      } else {
        this.selectedResourceTypeIdList.update((old) => [...old, category.resourceTypeId]);
      }
    } else {
      this.clearCategoryAndResourceFilterSelection();
    }
  }

  filterToSpecificCategory(category: QRCategoryItemModel) {
    if (category != null) {
      const indexOfSelectedItem = this.getIndexOfSelectedItem(category.qrCategoryId, this.selectedCategoryIdList());
      if (indexOfSelectedItem > -1) {
        this.selectedCategoryIdList.set(this.selectedCategoryIdList().filter((x) => x != category.qrCategoryId));
      } else {
        this.selectedCategoryIdList.update((old) => [...old, category.qrCategoryId]);
      }
    } else {
      this.clearCategoryAndResourceFilterSelection();
    }
  }

  updateData(filter: TargetFilterData) {
    this.selectedAreaIdList.set([...filter.selectedAreaIdList]);
    this.selectedCategoryIdList.set([...filter.selectedCategoryIdList]);
    this.selectedResourceTypeIdList.set([...filter.selectedResourceTypeIdList]);
  }

  toggleShow(item: NavigationBasicInfoWithFirstLetter, event: Event) {
    event.stopPropagation();
    item.searchResultIsShown = !item.searchResultIsShown;
  }
}
